// Sport rules: a short table of moves — heel hook, kani basami, suplex — each
// with its standing in every art: legal, banned, or legal with a condition in
// words ("IBJJF · brown & black · no-gi"). The table is the `rules`
// collection; the Sport Rules modal in Settings edits it. A technique attaches
// to a move by its optional `rule` field, or, failing that, by name: a clip
// called "Heel Hook from 50/50" falls under the heel hook row on its own.
import { collection, deleteDoc, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { useGuest } from '../guestMode';
import { Art } from '../types';
import type { Technique } from '../types';
import { ARTS, ART_META } from '../data/arts';

export const BANNED = 'banned';
/** art -> BANNED or the condition in words. An art missing here is legal. */
export type Legality = Partial<Record<Art, string>>;

export interface Rule {
  id: string;
  /** The move, as the table shows it: "Heel hook". */
  name: string;
  /** Other names it goes by, for matching techniques: "heelhook", "kani basami". */
  aliases: string[];
  /** Names that look like the move but aren't: "twister hook" is not the
   *  twister. A technique carrying one of these never matches this row. */
  except: string[];
  legality: Legality;
}

/** The conditions offered in each art's rotation before "banned". */
export type Presets = Partial<Record<Art, string[]>>;
export const DEFAULT_PRESETS: Presets = {
  [Art.Bjj]: ['IBJJF · brown & black', 'IBJJF · brown & black · no-gi', 'IBJJF · blue & up'],
  [Art.Judo]: [],
  [Art.Wrestling]: [],
};
const PRESETS_DOC = 'settings/legality';

/** Read a Firestore value as a legality map, dropping anything malformed. */
export function readLegality(raw: unknown): Legality {
  const out: Legality = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const a of ARTS) {
    const v = (raw as Record<string, unknown>)[a.id];
    if (typeof v === 'string' && v.trim()) out[a.id] = v.trim();
  }
  return out;
}

/** The rules table, live. */
export function useRules(): Rule[] {
  const [rules, setRules] = useState<Rule[]>([]);
  // The rules collection needs auth (firestore.rules), so a signed-out guest's
  // listener is denied and the list stays empty — which silently switches off
  // both hiddenByRules() and legalityNote(). Guests read the public copy that
  // homestats serves instead.
  //
  // Measured against the library as it stood: of 190 techniques, 2 match any
  // rule (Wrist Lock and Octo Wrist Lock, via wrist-lock) and NONE match one
  // banned in bjj — so this hid nothing from guests that members could not
  // also see, and cost them only the "IBJJF · blue & up" note on those two.
  // It is kept for what happens next: a technique matching a banned rule would
  // otherwise show to guests, unhidden and unlabelled, with nothing to signal
  // the divergence. Don't let this branch rot on the assumption it is inert.
  const { guest, stats } = useGuest();
  useEffect(() => {
    if (guest) return;
    return onSnapshot(collection(db, 'rules'), (snap) => {
      setRules(snap.docs.map((d) => {
        const x = d.data();
        return {
          id: d.id,
          name: typeof x.name === 'string' ? x.name : d.id,
          aliases: Array.isArray(x.aliases) ? x.aliases.filter((s: unknown): s is string => typeof s === 'string' && !!s.trim()) : [],
          except: Array.isArray(x.except) ? x.except.filter((s: unknown): s is string => typeof s === 'string' && !!s.trim()) : [],
          legality: readLegality(x.legality),
        };
      }).sort((a, b) => a.name.localeCompare(b.name)));
    }, () => { /* offline or unreadable: no rules */ });
  }, [guest]);

  if (guest) {
    return ((stats?.rules ?? []) as RuleDoc[])
      .map((x) => ({
        id: x.id,
        name: typeof x.name === 'string' ? x.name : x.id,
        aliases: Array.isArray(x.aliases) ? x.aliases : [],
        except: Array.isArray(x.except) ? x.except : [],
        legality: readLegality(x.legality),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  return rules;
}

/** One row of the public rules copy (see exports.homestats). */
interface RuleDoc {
  id: string;
  name?: string;
  aliases?: string[];
  except?: string[];
  legality?: unknown;
}

/** Write one rule (admin only, by Firestore rules). Merges, so a cell edit
 *  doesn't clobber a rename in flight. */
export async function saveRule(rule: Pick<Rule, 'id'> & Partial<Rule>): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (rule.name !== undefined) patch.name = rule.name.trim();
  if (rule.aliases !== undefined) patch.aliases = rule.aliases.map((s) => s.trim()).filter(Boolean);
  if (rule.except !== undefined) patch.except = rule.except.map((s) => s.trim()).filter(Boolean);
  if (rule.legality !== undefined) patch.legality = rule.legality;
  await setDoc(doc(db, 'rules', rule.id), patch, { merge: true });
}

/** Drop a move from the table (admin only, by Firestore rules). */
export async function deleteRule(id: string): Promise<void> {
  await deleteDoc(doc(db, 'rules', id));
}

/** A doc id from a move's name: "Heel hook" -> "heel-hook". */
export function ruleIdFor(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'rule';
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** A `rule` value that opts a technique out of matching: a "Twister Hook"
 *  is not the twister. */
export const NO_RULE = 'none';

/** The rule a technique falls under: its own `rule` field first (NO_RULE
 *  means none, whatever the name says), else the first rule whose name or
 *  alias appears in the technique's name or aliases as whole words.
 *  Undefined when nothing applies. */
export function ruleFor(tech: Pick<Technique, 'rule' | 'name' | 'aliases' | 'japaneseName'>, rules: Rule[]): Rule | undefined {
  if (tech.rule === NO_RULE) return undefined;
  if (tech.rule) {
    const r = rules.find((x) => x.id === tech.rule);
    if (r) return r;
  }
  const hay = [tech.name, tech.japaneseName, ...(tech.aliases ?? [])].filter((s): s is string => !!s).map((s) => ` ${norm(s)} `);
  for (const r of rules) {
    const blocked = r.except.map(norm).filter(Boolean);
    if (blocked.some((n) => hay.some((h) => h.includes(` ${n} `)))) continue;
    const needles = [r.name, ...r.aliases].map(norm).filter(Boolean);
    if (needles.some((n) => hay.some((h) => h.includes(` ${n} `)))) return r;
  }
  return undefined;
}

/** Hidden from the library: the user has narrowed to one art and the move is
 *  banned there. Two arts selected never hides — it's legal somewhere they
 *  train, so it shows with its note instead. */
export function hiddenByRules(tech: Pick<Technique, 'rule' | 'name' | 'aliases' | 'japaneseName'>, rules: Rule[], arts: Art[], showBanned: boolean): boolean {
  if (showBanned || arts.length !== 1 || !rules.length) return false;
  return ruleFor(tech, rules)?.legality[arts[0]] === BANNED;
}

/** The note a card or player shows: the condition (or "Banned") for the arts
 *  in view. One art: just the words. More than one, or none given: each
 *  art's note with its name in front, so the reader knows which sport. */
export function legalityNote(tech: Pick<Technique, 'rule' | 'name' | 'aliases' | 'japaneseName'>, rules: Rule[], arts?: Art[]): string | undefined {
  const l = ruleFor(tech, rules)?.legality;
  if (!l) return undefined;
  const scope = arts && arts.length ? ARTS.filter((a) => arts.includes(a.id)) : ARTS;
  const parts = scope.filter((a) => l[a.id]).map((a) => {
    const text = l[a.id] === BANNED ? 'Banned' : l[a.id]!;
    return scope.length === 1 ? text : `${ART_META[a.id].shortLabel}: ${text}`;
  });
  return parts.length ? parts.join(' · ') : undefined;
}

/** The presets doc, live; defaults until it exists. */
export function usePresets(): Presets {
  const [presets, setPresets] = useState<Presets>(DEFAULT_PRESETS);
  useEffect(() => {
    return onSnapshot(doc(db, PRESETS_DOC), (snap) => {
      const raw = snap.data()?.presets as Record<string, unknown> | undefined;
      if (!raw) return;
      const next: Presets = { ...DEFAULT_PRESETS };
      for (const a of ARTS) {
        const list = raw[a.id];
        if (Array.isArray(list)) next[a.id] = list.filter((s): s is string => typeof s === 'string' && !!s.trim());
      }
      setPresets(next);
    }, () => { /* offline or unreadable: keep defaults */ });
  }, []);
  return presets;
}

/** Add a condition to an art's rotation (admin only, by rules). */
export async function addPreset(current: Presets, art: Art, text: string): Promise<void> {
  const t = text.trim();
  if (!t) return;
  const list = [...(current[art] ?? [])];
  if (!list.includes(t)) list.push(t);
  const presets = { ...current, [art]: list };
  await setDoc(doc(db, PRESETS_DOC), { presets }, { merge: true });
}

/** The next value after `current` in an art's rotation:
 *  legal -> each preset -> banned -> legal. A value not in the rotation (a
 *  one-off condition) steps to banned, so the cycle always ends the same way. */
export function rotate(current: string | undefined, presets: string[]): string | undefined {
  const ring: (string | undefined)[] = [undefined, ...presets, BANNED];
  const i = ring.indexOf(current);
  if (i === -1) return BANNED;
  return ring[(i + 1) % ring.length];
}
